import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import {
    FolderOutlined,
    TagOutlined,
    CalendarOutlined,
    UserOutlined,
    AppstoreOutlined,
    BankOutlined,
    HomeOutlined,
    ArrowDownOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { Briefcase, Home, Filter, Search, RefreshCw, Users } from 'lucide-react';
import ProjectList from '../../components/Projects/ProjectList';
import { projects } from '../../data/projects';
import styles from './Projects.module.scss';

const cx = classNames.bind(styles);

// Hàm lấy danh sách category duy nhất từ mảng dự án
const getUniqueCategories = (projects) => {
    const categories = projects.map((project) => project.category);
    return ['Tất cả', ...new Set(categories)];
};

// Hàm lấy danh sách tags duy nhất từ mảng dự án
const getUniqueTags = (projects) => {
    const tagsArray = projects.reduce((acc, project) => [...acc, ...project.tags], []);
    return ['Tất cả', ...new Set(tagsArray)];
};

function Projects() {
    const [filteredProjects, setFilteredProjects] = useState(projects);
    const [activeCategory, setActiveCategory] = useState('Tất cả');
    const [activeTag, setActiveTag] = useState('Tất cả');
    const [activeProjectType, setActiveProjectType] = useState('Tất cả');
    const [searchQuery, setSearchQuery] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const [isFiltersVisible, setIsFiltersVisible] = useState(false);

    // State cho filter tab active
    const [activeFilterTab, setActiveFilterTab] = useState('all');

    // State hiển thị tất cả categories
    const [showAllCategories, setShowAllCategories] = useState(false);

    // Đánh dấu dự án loại gì (cá nhân, nhóm hay công ty)
    const projectTypes = ['Tất cả', 'Cá nhân', 'Nhóm', 'Công ty'];

    // Danh sách category và tags
    const categories = getUniqueCategories(projects);
    const tags = getUniqueTags(projects);
    // const tags = ['ReactJS', 'Ant Design', 'Redux', 'Spring Boot', 'Spring Security', 'Redis', 'Kafka', 'JWT', 'MySQL'];

    useEffect(() => {
        // Animation khi component mount
        setIsVisible(true);

        // Clean up function
        return () => {
            setIsVisible(false);
        };
    }, []);

    // Responsive filter toggle
    const toggleFilters = () => {
        setIsFiltersVisible(!isFiltersVisible);
    };

    // Lọc dự án khi category, tag, project type hoặc search thay đổi
    useEffect(() => {
        let result = [...projects];

        // Lọc theo loại dự án (Cá nhân/Nhóm/Công ty)
        if (activeProjectType !== 'Tất cả') {
            const projectTypeKey =
                activeProjectType === 'Cá nhân' ? 'personal' : activeProjectType === 'Nhóm' ? 'team' : 'company';
            result = result.filter((project) => project.type === projectTypeKey);
        }

        // Lọc theo category
        if (activeCategory !== 'Tất cả') {
            result = result.filter((project) => project.category === activeCategory);
        }

        // Lọc theo tag
        if (activeTag !== 'Tất cả') {
            result = result.filter((project) => project.tags.includes(activeTag));
        }

        // Lọc theo search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(
                (project) =>
                    project.title.toLowerCase().includes(query) ||
                    project.shortDescription.toLowerCase().includes(query),
            );
        }

        setFilteredProjects(result);
    }, [activeCategory, activeTag, activeProjectType, searchQuery]);

    // Xử lý thay đổi category
    const handleCategoryChange = (category) => {
        setActiveCategory(category);
    };

    // Xử lý thay đổi tag
    const handleTagChange = (tag) => {
        setActiveTag(tag);
    };

    // Xử lý thay đổi loại dự án
    const handleProjectTypeChange = (type) => {
        setActiveProjectType(type);
    };

    // Xử lý thay đổi search
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Reset all filters
    const resetFilters = () => {
        setActiveCategory('Tất cả');
        setActiveTag('Tất cả');
        setActiveProjectType('Tất cả');
        setSearchQuery('');
    };

    return (
        <div className={cx('projects-container')}>
            <div className={cx('projects-header', { visible: isVisible })}>
                <h1 className={cx('header-title')}>Dự án của tôi</h1>
                <p className={cx('header-subtitle')}>
                    Khám phá bộ sưu tập dự án đa dạng mà tôi đã thực hiện, từ ứng dụng web mạnh mẽ và giải pháp di động
                    đến các hệ thống phân tích dữ liệu tiên tiến.
                </p>

                {/* Thêm thống kê để trang trí và làm nổi bật */}
                <div className={cx('header-stats')}>
                    <div className={cx('stat-item')}>
                        <span className={cx('stat-value')}>{projects.filter((p) => p.type === 'personal').length}</span>
                        <span className={cx('stat-label')}>Dự án cá nhân</span>
                    </div>
                    <div className={cx('stat-item')}>
                        <span className={cx('stat-value')}>{projects.filter((p) => p.type === 'team').length}</span>
                        <span className={cx('stat-label')}>Dự án nhóm</span>
                    </div>
                    <div className={cx('stat-item')}>
                        <span className={cx('stat-value')}>{projects.filter((p) => p.type === 'company').length}</span>
                        <span className={cx('stat-label')}>Dự án công ty</span>
                    </div>
                    <div className={cx('stat-item')}>
                        <span className={cx('stat-value')}>
                            {Array.from(new Set(projects.map((p) => p.category))).length}
                        </span>
                        <span className={cx('stat-label')}>Loại dự án</span>
                    </div>
                </div>
            </div>

            {/* Mobile Filter Toggle Button */}
            <div className={cx('mobile-filter-toggle')}>
                <button onClick={toggleFilters} className={cx('toggle-button')}>
                    <Filter size={16} />
                    <span>Bộ lọc</span>
                    {isFiltersVisible ? '▲' : '▼'}
                </button>
            </div>

            <div className={cx('filters-container', { visible: isVisible, expanded: isFiltersVisible })}>
                {/* Search Bar */}
                <div className={cx('search-container')}>
                    <div className={cx('search-wrapper')}>
                        <Search size={18} className={cx('search-icon')} />
                        <input
                            type="text"
                            placeholder="Tìm kiếm dự án..."
                            className={cx('search-input')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <button className={cx('clear-search')} onClick={() => setSearchQuery('')}>
                                ×
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs cho filter */}
                <div className={cx('filter-tabs')}>
                    <div
                        className={cx('filter-tab', { active: activeFilterTab === 'all' })}
                        onClick={() => setActiveFilterTab('all')}
                    >
                        <TagOutlined className={cx('tab-icon')} /> Tất cả
                    </div>
                    <div
                        className={cx('filter-tab', { active: activeFilterTab === 'category' })}
                        onClick={() => setActiveFilterTab('category')}
                    >
                        <FolderOutlined className={cx('tab-icon')} /> Phân loại
                    </div>
                    <div
                        className={cx('filter-tab', { active: activeFilterTab === 'tech' })}
                        onClick={() => setActiveFilterTab('tech')}
                    >
                        <AppstoreOutlined className={cx('tab-icon')} /> Công nghệ
                    </div>
                </div>

                {/* Tab Content - All */}
                <div className={cx('tab-content', { active: activeFilterTab === 'all' })}>
                    {/* Filter Project Type */}
                    <div className={cx('filter-section')}>
                        <h3 className={cx('filter-title')}>
                            <BankOutlined className={cx('filter-icon')} /> Phân loại dự án
                        </h3>
                        <div className={cx('filter-pills')}>
                            {projectTypes.map((type, index) => (
                                <button
                                    key={index}
                                    className={cx('filter-pill', { active: activeProjectType === type })}
                                    onClick={() => handleProjectTypeChange(type)}
                                >
                                    {type === 'Cá nhân' ? (
                                        <Home size={14} className={cx('pill-icon')} />
                                    ) : type === 'Nhóm' ? (
                                        <Users size={14} className={cx('pill-icon')} />
                                    ) : type === 'Công ty' ? (
                                        <Briefcase size={14} className={cx('pill-icon')} />
                                    ) : null}
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Hiển thị các filter phổ biến nhất */}
                    <div className={cx('filter-section')}>
                        <h3 className={cx('filter-title')}>
                            <FolderOutlined className={cx('filter-icon')} /> Loại dự án
                        </h3>
                        <div className={cx('filter-pills')}>
                            {categories.slice(0, showAllCategories ? categories.length : 5).map((category, index) => (
                                <button
                                    key={index}
                                    className={cx('filter-pill', { active: activeCategory === category })}
                                    onClick={() => handleCategoryChange(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                        {categories.length > 5 && (
                            <div
                                className={cx('show-more', { expanded: showAllCategories })}
                                onClick={() => setShowAllCategories(!showAllCategories)}
                            >
                                {showAllCategories ? 'Thu gọn' : 'Xem thêm'}
                                <ArrowDownOutlined className={cx('more-icon')} />
                            </div>
                        )}
                    </div>

                    {/* Top tags */}
                    <div className={cx('filter-section')}>
                        <h3 className={cx('filter-title')}>
                            <TagOutlined className={cx('filter-icon')} /> Công nghệ phổ biến
                        </h3>
                        <div className={cx('filter-pills')}>
                            {tags.slice(0, 10).map((tag, index) => (
                                <button
                                    key={index}
                                    className={cx('filter-pill', { active: activeTag === tag })}
                                    onClick={() => handleTagChange(tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Tab Content - Category */}
                <div className={cx('tab-content', { active: activeFilterTab === 'category' })}>
                    <div className={cx('filter-section')}>
                        <h3 className={cx('filter-title')}>
                            <FolderOutlined className={cx('filter-icon')} /> Loại dự án
                        </h3>
                        <div className={cx('filter-pills')}>
                            {categories.map((category, index) => (
                                <button
                                    key={index}
                                    className={cx('filter-pill', { active: activeCategory === category })}
                                    onClick={() => handleCategoryChange(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Tab Content - Tech */}
                <div className={cx('tab-content', { active: activeFilterTab === 'tech' })}>
                    <div className={cx('filter-section')}>
                        <h3 className={cx('filter-title')}>
                            <TagOutlined className={cx('filter-icon')} /> Công nghệ
                        </h3>
                        <div className={cx('filter-pills')}>
                            {tags.map((tag, index) => (
                                <button
                                    key={index}
                                    className={cx('filter-pill', { active: activeTag === tag })}
                                    onClick={() => handleTagChange(tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Reset Filters Button */}
                {(activeCategory !== 'Tất cả' ||
                    activeTag !== 'Tất cả' ||
                    activeProjectType !== 'Tất cả' ||
                    searchQuery) && (
                    <div className={cx('reset-filters-container')}>
                        <button className={cx('reset-filters-button')} onClick={resetFilters}>
                            <RefreshCw size={14} />
                            Đặt lại bộ lọc
                        </button>
                    </div>
                )}
            </div>

            {/* Projects Grid - Using existing ProjectList component */}
            <ProjectList projects={filteredProjects} isVisible={isVisible} />

            {/* Empty State */}
            {filteredProjects.length === 0 && (
                <div className={cx('empty-state')}>
                    <div className={cx('empty-state-icon')}>🔍</div>
                    <h2>Không tìm thấy dự án phù hợp.</h2>
                    <p>Vui lòng thử lại với các bộ lọc khác.</p>
                    <button className={cx('reset-button')} onClick={resetFilters}>
                        <RefreshCw size={16} className={cx('reset-icon')} />
                        Đặt lại bộ lọc
                    </button>
                </div>
            )}
        </div>
    );
}

export default Projects;
